import {
  all,
  call,
  delay,
  put,
  race,
  SagaReturnType,
  select, take,
  takeEvery,
  takeLeading,
} from 'redux-saga/effects'

import { actions, selectors } from '..'
// eslint-disable-next-line import/no-cycle
import AuthSagas from '../auth/sagas'
import { ApiResponse } from '../api/types/state'
import { services } from '../../graphql'
import ApiSagas from '../api/sagas'
import {
  AppAcceptDisclaimerMutationVariables,
  AppCancelAppointementMutationVariables,
  AppConfirmAppointementMutationVariables,
  AppRdvUpdateMessageAppointmentMutationVariables,
} from '../../graphql/types/api'
import { Screen } from '../../types/global'

export default class AppSagas {
  static *init() {
    const isInit: SagaReturnType<typeof selectors.app.isInit> = yield select(selectors.app.isInit)
    yield call(AuthSagas.onInit) // must be first auth call to refresh token if needed
    yield call(AppSagas.loadInitialData)

    if (!isInit) {
      yield put(actions.app.setIsInit({ isInit: true }))
    }
  }

  static *loadInitialData() {
    yield all([call(AppSagas.loadAuthData)])
  }

  static *loadAuthData() {
    const isConnected: ReturnType<typeof selectors.auth.isConnected> = yield select(
      selectors.auth.isConnected
    )

    if (isConnected) {
      yield all([
        call(AuthSagas.onGetMeRequest),
        call(AppSagas.getStoresRequest),
        call(AppSagas.getDisclaimersRequest),
      ])
    }
  }

  static *getStoresRequest() {
    const response: ApiResponse<typeof services.app.queries.getStores> = yield call(
      ApiSagas.query,
      services.app.queries.getStores
    )

    if (response.errors) {
      yield put(actions.app.getStoresError(response.errors))
    }

    if (response.data) {
      yield put(actions.app.getStoresSuccess(response.data))
    }
  }

  static *getDisclaimersRequest() {
    const response: ApiResponse<typeof services.app.queries.getDisclaimers> = yield call(
      ApiSagas.query,
      services.app.queries.getDisclaimers
    )

    if (response.errors) {
      yield put(actions.app.getDisclaimersError(response.errors))
    }

    if (response.data) {
      yield put(actions.app.getDisclaimersSuccess(response.data))
    }
  }

  static *acceptDisclaimerRequest({
    payload,
  }: ReturnType<typeof actions.app.acceptDisclaimerRequest>) {
    const response: ApiResponse<typeof services.app.mutations.acceptDisclaimer> = yield call(
      ApiSagas.mutate,
      services.app.mutations.acceptDisclaimer,
      {
        ...payload,
      } as AppAcceptDisclaimerMutationVariables
    )

    if (response.errors) {
      yield put(actions.app.acceptDisclaimerError(response.errors))
    }

    if (response.data) {
      yield put(actions.app.acceptDisclaimerSuccess(response.data))
    }
  }

  static *confirmAppointementRequest({
    payload,
  }: ReturnType<typeof actions.app.confirmAppointementRequest>) {
    const response: ApiResponse<typeof services.app.mutations.comfirnAppointement> = yield call(
      ApiSagas.mutate,
      services.app.mutations.comfirnAppointement,
      {
        ...payload,
      } as AppConfirmAppointementMutationVariables
    )

    if (response.errors) {
      yield put(actions.app.confirmAppointementError(response.errors))
    }

    if (response.data) {
      // reload user data
      yield put(actions.auth.getMeRequest({}))
      // wait for user data update
      yield race([take(actions.auth.getMeSuccess), take(actions.auth.getMeError)])
      yield put(actions.app.confirmAppointementSuccess(response.data))
    }
  }

  static *cancelAppointementRequest({
    payload,
  }: ReturnType<typeof actions.app.cancelAppointementRequest>) {
    const response: ApiResponse<typeof services.app.mutations.cancelAppointement> = yield call(
      ApiSagas.mutate,
      services.app.mutations.cancelAppointement,
      {
        ...payload,
      } as AppCancelAppointementMutationVariables
    )

    if (response.errors) {
      yield put(actions.app.cancelAppointementError(response.errors))
    }

    if (response.data) {
      // reload user data
      yield put(actions.auth.getMeRequest({}))
      // wait for user data update
      yield race([take(actions.auth.getMeSuccess), take(actions.auth.getMeError)])
      yield put(actions.app.cancelAppointementSuccess(response.data))
    }
  }

  static *editAppointementRequest({
    payload,
  }: ReturnType<typeof actions.app.rdvUpdateMessageAppointmentRequest>) {
    const response: ApiResponse<
      typeof services.app.mutations.rdvUpdateMessageAppointment
    > = yield call(ApiSagas.mutate, services.app.mutations.rdvUpdateMessageAppointment, {
      ...payload,
    } as AppRdvUpdateMessageAppointmentMutationVariables)

    if (response.errors) {
      yield put(actions.app.rdvUpdateMessageAppointmentError(response.errors))
    }

    if (response.data) {
      yield put(actions.app.rdvUpdateMessageAppointmentSuccess(response.data))
      // reload user data
      yield put(actions.auth.getMeRequest({}))
      yield delay(1000)
      yield put(
        actions.app.setScreen({
          screen: Screen.appointement,
        })
      )
    }
  }

  static *loop() {
    yield all([
      takeLeading('persist/REHYDRATE', this.init),
      takeEvery(actions.app.getStoresRequest, this.getStoresRequest),
      takeEvery(actions.app.getDisclaimersRequest, this.getDisclaimersRequest),
      takeEvery(actions.app.acceptDisclaimerRequest, this.acceptDisclaimerRequest),
      takeEvery(actions.app.confirmAppointementRequest, this.confirmAppointementRequest),
      takeEvery(actions.app.cancelAppointementRequest, this.cancelAppointementRequest),
      takeEvery(actions.app.rdvUpdateMessageAppointmentRequest, this.editAppointementRequest),
    ])
  }
}
